import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Radio } from '@atlaskit/radio';
import Textfield from '@atlaskit/textfield';
import { FormValues, HowHeard } from '../../../types/ChampionApplicationFormTypes';

type FieldErrors = {
  howHeard?: string;
  howHeardOther?: string;
  referrerName?: string;
};

type Props = {
  values: FormValues;
  updateField: <K extends keyof FormValues>(key: K, value: FormValues[K]) => void;
  errors?: FieldErrors;
};

const OPTIONS: { id: NonNullable<HowHeard>; label: string }[] = [
  { id: 'ea', label: 'Atlassian Enterprise Advocate (EA)' },
  { id: 'tam', label: 'Atlassian Technical Account Manager (TAM)' },
  { id: 'csm', label: 'Atlassian Customer Success Manager' },
  { id: 'employee', label: 'Atlassian employee' },
  { id: 'champion', label: 'Atlassian Champion' },
  { id: 'solution_partner', label: 'Atlassian Solution Partner' },
  { id: 'community_website', label: 'Atlassian Community website' },
  { id: 'email_from_atlassian', label: 'Email from Atlassian' },
  { id: 'corporate_event', label: 'Atlassian corporate event (for example, Team)' },
  { id: 'social_media', label: 'Social media (for example, Facebook, LinkedIn, X)' },
  { id: 'coworker', label: 'Someone I work with' },
  { id: 'other_user_group', label: 'Another user group (Agile, DevOps, project management, etc.)' },
  { id: 'other', label: 'Other (please specify)' },
];

export const HowHeardSection: React.FC<Props> = ({ values, updateField, errors }) => {
  const setHowHeard = (id: HowHeard) => () => updateField('howHeard', id);

  const showOtherField = values.howHeard === 'other';

  return (
    <fieldset css={sectionBoxStyles}>
      <legend css={legendLabelStyles}>
        <span css={requiredStyles}>*</span>{' '}
        How did you find out about the Atlassian Champions program?
      </legend>

      <div css={controlTopStyles}>
        {OPTIONS.map((opt) => (
          <Radio
            key={opt.id}
            name="how_heard"
            label={opt.label}
            value={opt.id}
            isChecked={values.howHeard === opt.id}
            onChange={setHowHeard(opt.id)}
          />
        ))}

        {errors?.howHeard && <div css={errorStyles}>{errors.howHeard}</div>}
      </div>

      {showOtherField && (
        <div css={fieldTopStyles}>
          <label css={labelStyles} htmlFor="how-heard-other">
            Please specify
          </label>
          <Textfield
            id="how-heard-other"
            value={values.howHeardOther}
            onChange={(e) => updateField('howHeardOther', (e.target as HTMLInputElement).value)}
          />
          {errors?.howHeardOther && <div css={errorStyles}>{errors.howHeardOther}</div>}
        </div>
      )}

      {values.howHeard === 'champion' && (
        <div css={fieldTopStyles}>
          <label css={labelStyles} htmlFor="referrer-name">
            Referrer Name
          </label>
          <Textfield
            id="referrer-name"
            value={values.referrerName}
            maxLength={80}
            onChange={(e) => updateField('referrerName', (e.target as HTMLInputElement).value)}
          />
          {errors?.referrerName && <div css={errorStyles}>{errors.referrerName}</div>}
        </div>
      )}
    </fieldset>
  );
};

const sectionBoxStyles = css({
  border: `1px solid ${token('color.border')}`,
  borderRadius: '4px',
  padding: token('space.300'),
  marginBottom: token('space.400'),
  backgroundColor: token('elevation.surface'),
});

const legendLabelStyles = css({
  fontWeight: 600,
  fontSize: '14px',
});

const controlTopStyles = css({
  marginTop: token('space.100'),
});

const fieldTopStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginTop: token('space.200'),
});

const labelStyles = css({
  fontWeight: 600,
  fontSize: '14px',
  marginBottom: token('space.050'),
});

const requiredStyles = css({
  color: token('color.text.danger'),
});

const errorStyles = css({
  color: token('color.text.danger'),
  fontSize: '12px',
  marginTop: token('space.050'),
});
